import React, { useEffect, useState } from "react";
import { Modal, Button, Form, Table, Spinner, Alert, InputGroup } from "react-bootstrap";
import { authFetch } from "../utils/authFetch";
import { toast } from "react-toastify";

export default function StornoPickerModal({ show, onHide, contactId, onPick }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [history, setHistory] = useState([]);
  const [filterText, setFilterText] = useState("");
  const [returnQtys, setReturnQtys] = useState({}); // iesire_ln_id_bxstocin_id -> qty_to_return

  const fetchHistory = async () => {
    if (!contactId) return;
    setLoading(true);
    setErr("");
    try {
      const data = await authFetch(`/iesire/client-history/${contactId}`);
      // parse result (might be Response or direct JSON depending on authFetch wrapping)
      let list = [];
      if (data && typeof data === "object" && !("ok" in data) && !("json" in data)) {
        list = data;
      } else if (data?.ok) {
        list = await data.json();
      } else {
        list = data || [];
      }
      setHistory(list);

      // Pre-fill return quantities
      const initialQtys = {};
      list.forEach((item) => {
        const key = `${item.iesire_ln_id}_${item.bxstocin_id}`;
        const remaining = Math.max(0, item.qty_sold - item.total_returned_for_lot);
        initialQtys[key] = remaining > 0 ? remaining.toFixed(3) : "";
      });
      setReturnQtys(initialQtys);
    } catch (e) {
      console.error(e);
      setErr(e.message || "Eroare la incarcarea istoricului de vanzari.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (show && contactId) {
      fetchHistory();
    } else {
      setHistory([]);
      setFilterText("");
    }
  }, [show, contactId]);

  const handleQtyChange = (key, val) => {
    setReturnQtys((prev) => ({
      ...prev,
      [key]: val,
    }));
  };

  const handlePick = (item) => {
    const key = `${item.iesire_ln_id}_${item.bxstocin_id}`;
    const qtyStr = returnQtys[key];
    const qty = parseFloat(String(qtyStr).replace(",", "."));

    if (isNaN(qty) || qty <= 0) {
      toast.error("Vă rugăm să introduceți o cantitate validă, mai mare decât 0.");
      return;
    }

    const remaining = item.qty_sold - item.total_returned_for_lot;
    if (qty > remaining + 0.0001) {
      if (!window.confirm(`Atenție! Cantitatea stornată (${qty}) depășește cantitatea disponibilă din lot (${remaining.toFixed(3)}). Sigur doriți să continuați?`)) {
        return;
      }
    }

    onPick(item, qty);
    onHide();
  };

  const filteredHistory = history.filter((item) => {
    const search = filterText.toLowerCase();
    const docNo = (item.doc_no || "").toLowerCase();
    const code = (item.part_code || "").toLowerCase();
    const name = (item.part_name || "").toLowerCase();
    const lot = (item.nr_lot || "").toLowerCase();

    return (
      docNo.includes(search) ||
      code.includes(search) ||
      name.includes(search) ||
      lot.includes(search)
    );
  });

  return (
    <Modal show={show} onHide={onHide} size="xl" centered scrollable>
      <Modal.Header closeButton className="bg-light">
        <Modal.Title className="text-primary fw-bold">Istoric Vânzări Client (Pentru Storno)</Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ minHeight: "60vh" }}>
        {err && <Alert variant="danger">{err}</Alert>}

        <Form.Group className="mb-3">
          <InputGroup>
            <Form.Control
              placeholder="Filtrează după număr factură, cod produs, denumire, lot..."
              value={filterText}
              onChange={(e) => setFilterText(e.target.value)}
              autoFocus
            />
            {filterText && (
              <Button variant="outline-secondary" onClick={() => setFilterText("")}>
                X
              </Button>
            )}
          </InputGroup>
        </Form.Group>

        {loading ? (
          <div className="d-flex flex-column align-items-center justify-content-center py-5">
            <Spinner animation="border" variant="primary" className="mb-2" />
            <span className="text-muted">Se încarcă istoricul loturilor vândute...</span>
          </div>
        ) : (
          <div className="table-responsive">
            <Table hover bordered striped size="sm" className="align-middle">
              <thead className="table-secondary">
                <tr>
                  <th>Factură / Dată</th>
                  <th>Gestiune</th>
                  <th>Produs (Cod & Denumire)</th>
                  <th>Nr. Lot</th>
                  <th className="text-end">Preț Unitar (Vânzare)</th>
                  <th className="text-end">TVA %</th>
                  <th className="text-end">Vândut Lot</th>
                  <th className="text-end">Stornat Anterior</th>
                  <th className="text-end" style={{ width: 140 }}>Cant. Storno</th>
                  <th className="text-center" style={{ width: 120 }}>Acțiune</th>
                </tr>
              </thead>
              <tbody>
                {filteredHistory.map((item) => {
                  const key = `${item.iesire_ln_id}_${item.bxstocin_id}`;
                  const remaining = item.qty_sold - item.total_returned_for_lot;
                  const dateStr = item.doc_date ? new Date(item.doc_date).toLocaleDateString("ro-RO") : "";

                  return (
                    <tr key={key} className={remaining <= 0 ? "table-light text-muted" : ""}>
                      <td>
                        <span className="fw-semibold">#{item.doc_no}</span>
                        <div className="text-muted small">{dateStr}</div>
                      </td>
                      <td>
                        <span className="small">{item.gestiune_code || item.bxgestiune_id}</span>
                        <div className="text-muted small">{item.gestiune_name}</div>
                      </td>
                      <td>
                        <span className="fw-bold">{item.part_code}</span>
                        <div>{item.part_name}</div>
                      </td>
                      <td>
                        {item.nr_lot ? (
                          <span className="badge bg-info text-dark">{item.nr_lot}</span>
                        ) : (
                          <span className="text-muted small">—</span>
                        )}
                      </td>
                      <td className="text-end fw-semibold">{item.price.toFixed(4)}</td>
                      <td className="text-end">{item.vat_rate}%</td>
                      <td className="text-end fw-bold">{item.qty_sold.toFixed(3)}</td>
                      <td className="text-end text-danger">{item.total_returned_for_lot.toFixed(3)}</td>
                      <td>
                        <Form.Control
                          size="sm"
                          type="text"
                          inputMode="decimal"
                          className="text-end fw-bold"
                          value={returnQtys[key] ?? ""}
                          disabled={remaining <= 0}
                          onChange={(e) => handleQtyChange(key, e.target.value)}
                        />
                      </td>
                      <td className="text-center">
                        <Button
                          size="sm"
                          variant="warning"
                          disabled={remaining <= 0}
                          onClick={() => handlePick(item)}
                        >
                          + Storno
                        </Button>
                      </td>
                    </tr>
                  );
                })}
                {filteredHistory.length === 0 && (
                  <tr>
                    <td colSpan={10} className="text-center py-4 text-muted">
                      Nu s-au găsit înregistrări în istoricul de vânzări.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="bg-light">
        <Button variant="secondary" onClick={onHide}>
          Închide
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
